import { useEffect, useState } from "react";

import {
  ArrowButton,
  AutocompleteMultipleSelect,
  Button,
  ColumnSortEnum,
  Select,
  Table,
  TableMetadata,
  TableStateWithFilters,
  TextField,
  Typography,
  useTable
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as DownloadIcon } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { ExportTypeEnum, PagedResult } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { InstitutionExportRequest } from "../../api/Institution/InstitutionExportRequest";
import { InstitutionSearchRequest } from "../../api/Institution/InstitutionSearchRequest";
import { searchMunicipalities, searchInstitutions, exportInstitutions } from "../../api/nisooApi";
import { useDidUpdateEffect, useInput } from "../../hooks";
import { InstitutionListItem, InstitutionSearch, InstitutionSort } from "../../models/Institution";
import { MunicipalityCodeBook } from "../../models/Municipality";
import { SortDirection } from "../../models/Shared";
import { useGetCountiesQuery, useGetProgramTypesQuery } from "../../queries/codeBookQuery";
import { institutionDetailsRoute } from "../../routes/routes";
import styles from "./InstitutionList.module.scss";

export function InstitutionList() {
  const storageKey = 'InstitutionSearchTableState';
  const createInitialTableState = (): TableStateWithFilters<InstitutionSearch> => {
    const sessionStorageTableState = sessionStorage.getItem(storageKey);
    if (sessionStorageTableState === null) {
      return {
        programTypeIds: [],
        institutionName: "",
        municipalities: [],
        countyIds: [],
        page: 1,
        pageSize: 10,
        sortBy: InstitutionSort.InstitutionName,
        order: ColumnSortEnum.DESC
      };
    }

    var result = JSON.parse(sessionStorageTableState) as TableStateWithFilters<InstitutionSearch>;
    return result;
  }

  const createInitialInstitutionsSearch = (initialTableState: TableStateWithFilters<InstitutionSearch>): InstitutionSearch => {
    return {
      programTypeIds: initialTableState.programTypeIds,
      institutionName: initialTableState.institutionName,
      municipalities: initialTableState.municipalities,
      countyIds: initialTableState.countyIds
    }
  }

  const createInitialInstitutions = (): PagedResult<InstitutionListItem> => {
    return {
      currentPage: 1,
      firstRowOnPage: 0,
      lastRowOnPage: 0,
      pageCount: 0,
      pageSize: 10,
      rowCount: 0,
      results: [],
    }
  }
  const [initialTableState] = useState<TableStateWithFilters<InstitutionSearch>>(() => createInitialTableState());
  const [filteredMunicipalities, setFilteredMunicipalities] = useState<MunicipalityCodeBook[]>([] as MunicipalityCodeBook[]);
  const [institutions, setInstitutions] = useState<PagedResult<InstitutionListItem>>(() => createInitialInstitutions());
  const [institutionSearch, setInstitutionSearch] = useState<InstitutionSearch>(() => createInitialInstitutionsSearch(initialTableState));
  const [lastUsedSearch, setLastUsedSearch] = useState<InstitutionSearchRequest>();

  const history = useHistory();
  const { t } = useTranslation();
  const { handleChange } = useInput();
  const { data: programTypes } = useGetProgramTypesQuery();
  const { data: counties } = useGetCountiesQuery();

  const institutionTableMetadata: TableMetadata<InstitutionListItem> = {
    rowKey: "institutionId",
    columns: [
      {
        columnKey: "institutionName",
        header: t("institution.institutionName"),
        accessor: "institutionName",
        color: "success",
        sortBy: InstitutionSort.InstitutionName
      },
      {
        columnKey: "institutionAddress",
        header: t("institution.headquarterAddress"),
        accessor: (item: InstitutionListItem) => <>{item.institutionAddress + ', ' + item.institutionTownName + ' ' + item.institutionTownPostalCode+ ', '} <br/> {item.institutionCountyName}</>,
        sortBy: InstitutionSort.InstitutionAddress
      },
      {
        columnKey: "programTypes",
        header: t("common.programType"),
        accessor: "programTypes",
        sortBy: InstitutionSort.ProgramTypes
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item) => {
          return (<div className={"text-right"}>
              <ArrowButton ariaLabel={t("request.goToInstitutionDetails")}
                onClick={() => onOpenInstitutionDetails(item.institutionId)}
              />
          </div>)
        }
      }
    ],
    rowToolTip: () => undefined
  };

  var [tableProps, tableState, setFilters] = useTable<InstitutionSearch>(
    {
      metadata: institutionTableMetadata,
      data: institutions.results,
      pagination: true,
      paginationPage: initialTableState.page,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: institutions.rowCount,
      defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
    }
  );

  useEffect(() => {
    const searchInstitutionsHelper = async () => {
      var request: InstitutionSearchRequest = {
        countyIds: institutionSearch.countyIds,
        municipalityIds: institutionSearch.municipalities.map(mun => mun.id),
        institutionName: institutionSearch.institutionName,
        programTypeIds: institutionSearch.programTypeIds,
        sortBy: tableState.sortBy ?? InstitutionSort.InstitutionName,
        sortDirection: tableState.order && tableState.order === ColumnSortEnum.ASC ? SortDirection.Asc : SortDirection.Desc,
        page: tableState.page ?? 1,
        pageSize: tableState.pageSize ?? 10
      };
      setLastUsedSearch(request);

      var results = await searchInstitutions(request);
      setInstitutions(results);
    }
    searchInstitutionsHelper();
  // programRequestSearch change shouldn't trigger this fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  useDidUpdateEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify({
      ...institutionSearch,
      sortBy: tableState.sortBy ?? InstitutionSort.InstitutionName,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? 10
    }));
  }, [tableState])

  const onOpenInstitutionDetails = (id: number) => {
    history.push(institutionDetailsRoute(), { id: id });
  };

  const handleMunicipalityFilterChange = async (filter: string) => {
    var result = await searchMunicipalities(filter, institutionSearch.countyIds);
    setFilteredMunicipalities(result);
  }

  const handleCountyChange = (e: any) => {
    const newCountyIds = e.target.value as number[];
    let newMunicipalities = institutionSearch.municipalities.slice();
    newMunicipalities = newMunicipalities.filter((mun) => newCountyIds.includes(mun.countyId));

    const newInstitutionSearch: InstitutionSearch = {
      ...institutionSearch,
      countyIds: newCountyIds,
      municipalities: newMunicipalities
    };
    setInstitutionSearch(newInstitutionSearch);
  }

  const handleMunicipalityChange = (e: any) => {
    const newMunicipalities = e.target.value as MunicipalityCodeBook[];
    const newInstitutionSearch: InstitutionSearch = {
      ...institutionSearch,
      municipalities: newMunicipalities.slice(0, 10)
    };
    setInstitutionSearch(newInstitutionSearch);
  }

  const clearFilters = () => {
    const emptySearch = {
      countyIds: [],
      municipalities: [],
      programTypeIds: [],
      institutionName: ""
    };
    setInstitutionSearch(emptySearch);
    setFilters(emptySearch);
  }

  const search = async () => {
    setFilters(institutionSearch);
  };

  const download = async (exportType: ExportTypeEnum) => {
    if (!lastUsedSearch) {
      return;
    }
    const request: InstitutionExportRequest = {
      countyIds: lastUsedSearch.countyIds,
      exportType: exportType,
      institutionName: lastUsedSearch.institutionName,
      municipalityIds: lastUsedSearch.municipalityIds,
      programTypeIds: lastUsedSearch.municipalityIds,
      sortBy: lastUsedSearch.sortBy,
      sortDirection: lastUsedSearch.sortDirection
    }
    exportInstitutions(request);
  }

  return (
    <>
      <Row>
        <Col md={12}>
          <h2>{t("navigation.institutionList")}</h2>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col md={6}>
          <Select
            multiple
            label={t("common.county")}
            name={nameof<typeof institutionSearch>("countyIds")}
            value={institutionSearch.countyIds}
            onChange={handleCountyChange}
            selectData={codeBookToSelectData(counties)}
          />
        </Col>
        <Col md={6}>
          <AutocompleteMultipleSelect<MunicipalityCodeBook>
            label={t("common.municipality")}
            name={nameof<typeof institutionSearch>("municipalities")}
            value={institutionSearch.municipalities}
            valueProp="id"
            textProp= "name"
            onChange={handleMunicipalityChange}
            selectOptions={filteredMunicipalities || []}
            onFilterChange={handleMunicipalityFilterChange}
            maximumSelectedItems={10}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <TextField
            label={t("institution.institutionName")}
            name={nameof<typeof institutionSearch>("institutionName")}
            value={institutionSearch.institutionName}
            maxLength={80}
            onChange={handleChange(setInstitutionSearch)}
          />
        </Col>
        <Col md={6}>
            <Select
              multiple
              label={t("request.programType")}
              name={nameof<typeof institutionSearch>("programTypeIds")}
              value={institutionSearch.programTypeIds}
              onChange={handleChange(setInstitutionSearch)}
              selectData={codeBookToSelectData(programTypes)}
            />
        </Col>
      </Row>
      <Row className="mb-5 mt-5">
        <Col md={12} className="d-flex justify-content-end align-items-center">
          <Button onClick={search}>
            {t("common.search")}
          </Button>
          <Typography color="primary" className={styles.clearFilters + " ml-3"} onClick={clearFilters}>
            {t("common.clearFilters")}
          </Typography>
        </Col>
      </Row>
      <Table {...tableProps} />
      <Row className="mt-5">
        <Col md={12} className="d-flex justify-content-end align-items-center">
        <Button
            className={styles.exportButton}
            outline
            color={"primary"}
            icon={<DownloadIcon />}
            onClick={() => download(ExportTypeEnum.Csv)}
          >
            {t("common.downloadDocumentExtension", { extension: ".csv"})}
          </Button>
          <Button
            className={styles.exportButton}
            outline
            color={"primary"}
            icon={<DownloadIcon />}
            onClick={() => download(ExportTypeEnum.Excel)}
          >
            {t("common.downloadDocumentExtension", { extension: ".xslx"})}
          </Button>
        </Col>
      </Row>
    </>
  );
}